import { Badge, Flex, Icon, Small, Text } from "orcs-design-system";

import {
  formatElapsedAgo,
  formatElapsedOverdue,
  liveElapsedHours,
  showPastSla,
  useClockNow,
} from "src/pages/WorkforceGuardrails/clockHours";

import InboxCardShell from "../InboxCardShell";
import { pillOf, stepLineOf, type RequisitionInboxItem } from "./item";

const ageOf = (
  item: RequisitionInboxItem,
  nowMs: number
): { age: string; overdue: string | null } => {
  switch (item.kind) {
    case "REQUISITION_APPROVAL": {
      const elapsed = liveElapsedHours(
        item.elapsedHours,
        item.clockStartedAt,
        nowMs
      );
      const overdue = showPastSla({
        slaHours: item.slaHours,
        pastSla: item.overdue,
        elapsedHours: item.elapsedHours,
        clockStartedAt: item.clockStartedAt,
        nowMs,
      })
        ? formatElapsedOverdue(
            elapsed != null && item.slaHours != null
              ? elapsed - item.slaHours
              : null
          )
        : null;
      return { age: formatElapsedAgo(elapsed), overdue };
    }
    case "REQUISITION_EXCEPTION_REASON":
      return {
        age: formatElapsedAgo(liveElapsedHours(null, item.since, nowMs)),
        overdue: null,
      };
    default: {
      const unexpected: never = item;
      return unexpected;
    }
  }
};

const RequisitionInboxCard = ({
  item,
  selected,
  arrived = false,
  onSelect,
}: {
  item: RequisitionInboxItem;
  selected: boolean;
  arrived?: boolean;
  onSelect: (key: string) => void;
}) => {
  const nowMs = useClockNow();
  const { age, overdue } = ageOf(item, nowMs);
  const pill = pillOf(item.kind);
  return (
    <InboxCardShell
      itemKey={item.key}
      selected={selected}
      arrived={arrived}
      onSelect={onSelect}
      kind={
        <Flex alignItems="center" gap="xs" flexWrap="wrap">
          <Text>Requisition</Text>
          <Badge variant={pill.variant}>{pill.label}</Badge>
        </Flex>
      }
      meta={
        <>
          <Small color="greyDark">
            <Icon icon={["far", "clock"]} mr="xs" />
            {age}
          </Small>
          {overdue ? (
            <Small color="danger">
              <Icon
                icon={["far", "stopwatch"]}
                color="danger"
                mr="xs"
                aria-hidden="true"
              />
              <span>{overdue}</span>
            </Small>
          ) : null}
        </>
      }
    >
      <Text weight="bold" display="block">
        {item.title}
      </Text>
      {item.subtitle ? (
        <Small color="greyDark" display="block">
          {item.subtitle}
        </Small>
      ) : null}
      <Small display="block" mt="xs">
        {stepLineOf(item)}
      </Small>
      {item.failingChecks.map((label) => (
        <Small key={label} color="warningDarker" display="block">
          <Icon icon={["far", "exclamation-triangle"]} mr="xs" />
          {label}
        </Small>
      ))}
    </InboxCardShell>
  );
};

export default RequisitionInboxCard;
